import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';

// ✅ Register once at module level
gsap.registerPlugin(ScrollTrigger, Flip);

// ✅ FIX: Pexels supports resize/compression via query params
// (auto=compress&cs=tinysrgb&w=...) — these images were being downloaded
// at full original resolution while only ever displayed at 600x323 (13:7
// card thumbnail). w=1200 is 2x that for retina screens, cutting the
// download size drastically without touching the CSS/layout at all.
const projects = [
  {
    id: 1,
    client: "Sarah Mitchell",
    brand: "Aura Fashion Group",
    platform: "TikTok Shop UK",
    metric: "2.4x Sales Growth",
    subMetric: "£84,000 Revenue in Month 1",
    text: "Complete TikTok Shop integration combined with creator affiliate matchmaking. Transformed their organic social presence into a highly optimized social commerce machine, doubling conversions within 30 days.",
    image: "https://images.pexels.com/photos/5585800/pexels-photo-5585800.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: 2,
    client: "James Cooper",
    brand: "Apex Moto Parts",
    platform: "eBay UK Enterprise",
    metric: "35M+ Impressions",
    subMetric: "+42% Year-over-Year Net Margin",
    text: "Deep-level eBay UK store restructuring, SEO optimization on 15,000+ skus, and cross-border shipping management setup. Scaled domestic visibility to dominate category search rankings.",
    image: "https://images.pexels.com/photos/7621352/pexels-photo-7621352.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: 3,
    client: "Emma Thompson",
    brand: "Lumina Skincare",
    platform: "Shopify Plus",
    metric: "74% ROAS Increase",
    subMetric: "Seamless UK Tax & Gateway Setup",
    text: "Designed and engineered a custom headless Shopify build optimized for mobile commerce. Implemented regional automated UK VAT rules, advanced retention funnels, and optimized multi-currency gateways.",
    image: "https://images.pexels.com/photos/11952301/pexels-photo-11952301.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: 4,
    client: "David Williams",
    brand: "Vanguard Logistics",
    platform: "Multi-Channel Automation",
    metric: "99.8% Order Accuracy",
    subMetric: "Fulfillment Costs Cut by 30%",
    text: "Architected a custom ERP middleware linking Shopify, eBay, and external fulfillment nodes. Automated tracking loops and supplier integrations, freeing up over 40 operational hours per week.",
    image: "https://images.pexels.com/photos/234352/pexels-photo-234352.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: 5,
    client: "Lisa Chen",
    brand: "Zora Living Co.",
    platform: "Brand Launch (Shopify)",
    metric: "£0 to £140k / Mo",
    subMetric: "Achieved in under 90 Days",
    text: "Ground-up private label brand strategy. Executed professional listing designs, premium copywriting, digital style guides, and targeted multi-channel audience building layouts.",
    image: "https://images.pexels.com/photos/17485349/pexels-photo-17485349.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    id: 6,
    client: "Mark Davis",
    brand: "Nomad Apparel",
    platform: "Omnichannel Scale",
    metric: "3 Engines, 1 Dashboard",
    subMetric: "Consolidated Sync Infrastructure",
    text: "Unified operational visibility for an active merchant scaling synchronously across TikTok Shop, Shopify, and eBay UK. Centralized cross-platform stock allocations to mitigate oversells completely.",
    image: "https://images.pexels.com/photos/7621358/pexels-photo-7621358.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

export default function ProjectShowcase() {
  const containerRef = useRef(null);
  const col1Ref = useRef(null);
  const col2Ref = useRef(null);
  const col3Ref = useRef(null);
  const floatWrapRef = useRef(null);
  const largeImgRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);

  const handleThumbClick = (project, e) => {
    e.stopPropagation();
    const card = e.currentTarget.closest('.project-card-item');
    const fullSize = largeImgRef.current;
    const floatWrap = floatWrapRef.current;

    const state = Flip.getState([fullSize, card], {
      props: "borderRadius,padding,maxWidth,maxHeight",
    });

    fullSize.src = project.image;
    fullSize.style.display = "block";
    floatWrap.style.display = "block";
    floatWrap.style.pointerEvents = "auto";
    setActiveProject(project);
    setIsFlipped(true);

    Flip.from(state, {
      targets: fullSize,
      xPercent: 0,
      yPercent: 0,
      duration: 1,
      ease: "sine.inOut",
    });
  };

  const handleLargeClick = () => {
    const floatWrap = floatWrapRef.current;
    const fullSize = largeImgRef.current;

    floatWrap.style.pointerEvents = "none";

    const targetCard = document.querySelector(`[data-project-id="${activeProject?.id}"]`);

    Flip.fit(fullSize, targetCard, {
      duration: 1,
      props: "padding,borderRadius,maxWidth,maxHeight",
      ease: "sine.inOut",
      onComplete: () => {
        gsap.set(fullSize, { clearProps: "transform" });
        fullSize.style.display = "none";
        floatWrap.style.display = "none";
        setIsFlipped(false);
        setActiveProject(null);
      },
    });
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ✅ FIX: removed ScrollTrigger.normalizeScroll(true) — this was a major
      // CLS culprit: it hijacks native scroll and causes layout recalculations
      // on every frame, directly contributing to your 0.993 CLS score.

      gsap.set(col2Ref.current, { yPercent: -80 });

      // ✅ yPercent uses translateY — compositor-safe, no layout reflow
      const slide = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });

      slide.to([col1Ref.current, col3Ref.current], { yPercent: -80, ease: "none" }, 0);

      gsap.to(col2Ref.current, {
        yPercent: 0,
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const renderProjectCard = (p, uniqueKey) => (
    <div
      key={uniqueKey}
      data-project-id={p.id}
      className="project-card-item"
      onClick={(e) => handleThumbClick(p, e)}
    >
      <div className="project-card-image">
        {/* ✅ FIX: explicit width+height on every card image — eliminates CLS
            from 12 images (6 cards × 2 columns duplicated) all loading async */}
        <img
          src={p.image}
          alt={p.brand}
          width={600}
          height={323}
          loading="lazy"
          decoding="async"
        />
        <div className="project-card-platform">{p.platform}</div>
        <div className="project-card-metric-badge">
          <h3>{p.metric}</h3>
          <span>{p.subMetric}</span>
        </div>
      </div>
      <div className="project-card-content">
        <span className="project-brand-name">{p.brand}</span>
        <p className="project-card-snippet">"{p.text.substring(0, 75)}..."</p>
        <div className="project-card-footer">
          <div><h4>{p.client}</h4></div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .showcase-section {
          position: relative;
          overflow: hidden;
        }
        .showcase-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transform: rotate(180deg);
          z-index: 0;
        }
        .showcase-section > * { position: relative; z-index: 1; }
        .showcase-spacer { width: 100%; height: 100px; }
        .showcase-header {
          text-align: center;
          padding: 50px 20px 30px;
          position: relative;
          z-index: 2;
        }
        .showcase-header span {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: #F97316;
          display: block;
          margin-bottom: 12px;
        }
        .showcase-header h2 {
          font-family: 'Instrument Serif', serif;
          font-size: clamp(38px, 6vw, 56px);
          font-weight: 400;
          color: #0A0A0A;
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin-bottom: 14px;
        }
        .showcase-header p {
          font-size: 16px;
          color: #555;
          max-width: 580px;
          margin: 0 auto;
          line-height: 1.6;
        }
        .flex { display: flex; }
        .flexCenter { align-content: center; justify-content: center; }
        .showcase-container {
          width: 100%;
          height: auto;
          min-height: 600px;
          padding: 0px 2em;
          margin: 0 auto;
          background-color: transparent;
          overflow: hidden;
          position: relative;
          gap: 16px;
        }
        .col {
          /* ✅ will-change:transform — yPercent uses translateY, compositor-safe */
          will-change: transform;
          width: 33.333%;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .project-card-item {
          background: #FFFFFF;
          border: 1px solid #EAEAEA;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.25, 1, 0.5, 1);
          box-shadow: 0 2px 10px rgba(0,0,0,0.01);
          font-family: 'epilogue', sans-serif;
        }
        .project-card-item:hover {
          border-color: #F97316;
          box-shadow: 0 8px 30px rgba(249,115,22,0.1);
          transform: translateY(-2px);
        }
        .project-card-image {
          position: relative;
          width: 100%;
          /* ✅ FIX: explicit aspect-ratio + width/height on img reserves
             space before image loads — this was a major CLS source */
          aspect-ratio: 13/7;
          overflow: hidden;
          background: #F0F0F0;
        }
        .project-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .project-card-item:hover .project-card-image img { transform: scale(1.04); }
        .project-card-platform {
          position: absolute;
          top: 10px; left: 10px;
          padding: 4px 10px;
          background: rgba(10,10,10,0.65);
          backdrop-filter: blur(2px);
          border-radius: 100px;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: #FFFFFF;
        }
        .project-card-metric-badge {
          position: absolute;
          bottom: 10px; right: 10px;
          background: #FFFFFF;
          padding: 6px 10px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
          border-left: 2px solid #F97316;
          max-width: 80%;
        }
        .project-card-metric-badge h3 {
          margin: 0;
          font-size: 13px;
          font-weight: 800;
          color: #0A0A0A;
          line-height: 1.2;
          font-family: 'epilogue', sans-serif;
        }
        .project-card-metric-badge span {
          font-size: 9px;
          color: #666;
          display: block;
          margin-top: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-family: 'epilogue', sans-serif;
        }
        .project-card-content { padding: 12px; font-family: 'epilogue', sans-serif; }
        .project-brand-name {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: #F97316;
          letter-spacing: 0.05em;
        }
        .project-card-snippet {
          font-size: 12.5px;
          color: #444;
          line-height: 1.4;
          margin: 6px 0 12px;
          font-family: 'epilogue', sans-serif;
        }
        .project-card-footer {
          display: flex;
          align-items: center;
          gap: 8px;
          border-top: 1px solid #F5F5F5;
          padding-top: 10px;
        }
        .project-card-footer h4 {
          font-size: 11px;
          font-weight: 600;
          color: #666;
          margin: 0;
          font-family: 'epilogue', sans-serif;
        }
        .floatWrap {
          position: fixed;
          top: 0; left: 0;
          width: 100%; height: 100vh;
          background-color: rgba(10,10,10,0.85);
          backdrop-filter: blur(8px);
          pointer-events: none;
          z-index: 999;
          display: none;
        }
        .floater {
          width: 90%;
          max-width: 500px;
          height: auto;
          z-index: 1000;
          position: absolute;
          top: 45%; left: 50%;
          transform: translate(-50%, -50%);
        }
        .largeImg {
          display: none;
          border-radius: 12px;
          cursor: pointer;
          box-shadow: 0 20px 50px rgba(0,0,0,0.4);
        }
        .floater-info {
          position: fixed;
          bottom: 0; left: 0;
          width: 100%;
          background: linear-gradient(to top, rgba(0,0,0,0.95) 80%, rgba(0,0,0,0));
          padding: 50px 20px 30px;
          z-index: 1001;
          text-align: center;
          pointer-events: none;
          display: none;
          font-family: 'epilogue', sans-serif;
        }
        .floater-info.active { display: block; }
        .floater-info-content { max-width: 580px; margin: 0 auto; }
        .floater-info .fi-metric { font-size: clamp(22px, 4vw, 28px); font-weight: 800; color: #F97316; margin-bottom: 4px; }
        .floater-info .fi-submetric { font-size: 12px; color: #FFF; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 14px; opacity: 0.8; }
        .floater-info .fi-text { font-size: 14px; color: #E5E5E5; line-height: 1.5; margin-bottom: 14px; }
        .floater-info .fi-brand { font-family: 'Instrument Serif', serif; font-size: 18px; color: #FFF; margin: 0; }
        .close-hint {
          position: fixed;
          top: 20px; right: 20px;
          z-index: 1001;
          pointer-events: none;
          display: none;
          color: rgba(255,255,255,0.6);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          background: rgba(255,255,255,0.1);
          padding: 6px 12px;
          border-radius: 100px;
        }
        .close-hint.active { display: block; }
        @media (max-width: 992px) {
          .showcase-container { flex-direction: column; height: auto; gap: 16px; }
          .col { width: 100%; flex-direction: row; flex-wrap: wrap; gap: 12px; }
          .project-card-item { width: calc(50% - 6px); }
        }
        @media (max-width: 600px) {
          .project-card-item { width: 100%; }
          .col { flex-direction: column; }
          .floater-info { padding: 30px 20px 20px; }
        }
      `}</style>

      <section className="showcase-section">
        <div className="showcase-spacer" />
        <div className="showcase-header">
          <span>Case Studies</span>
          <h2>Projects & Real Results</h2>
          <p>
            An inside look at how we scale GMV, optimize structures, and build bespoke infrastructure across TikTok Shop, Shopify, and eBay UK.
          </p>
        </div>

        <div ref={containerRef} className="flex flexCenter showcase-container">
          <div ref={col1Ref} className="col col1">
            {projects.slice(0, 2).map(p => renderProjectCard(p, `p1-${p.id}`))}
            {projects.slice(0, 2).map(p => renderProjectCard(p, `dup1-${p.id}`))}
          </div>
          <div ref={col2Ref} className="col col2">
            {projects.slice(2, 4).map(p => renderProjectCard(p, `p2-${p.id}`))}
            {projects.slice(2, 4).map(p => renderProjectCard(p, `dup2-${p.id}`))}
          </div>
          <div ref={col3Ref} className="col col3">
            {projects.slice(4, 6).map(p => renderProjectCard(p, `p3-${p.id}`))}
            {projects.slice(4, 6).map(p => renderProjectCard(p, `dup3-${p.id}`))}
          </div>
        </div>

        <div ref={floatWrapRef} className="floatWrap" onClick={handleLargeClick}>
          <img ref={largeImgRef} className="floater largeImg" src="" alt="Enlarged Project Overview" />
        </div>

        <div className={`floater-info ${isFlipped ? 'active' : ''}`}>
          <div className="floater-info-content">
            <div className="fi-metric">{activeProject?.metric}</div>
            <div className="fi-submetric">{activeProject?.subMetric}</div>
            <p className="fi-text">{activeProject?.text}</p>
            <p className="fi-brand">{activeProject?.brand} — Case Study</p>
          </div>
        </div>

        <div className={`close-hint ${isFlipped ? 'active' : ''}`}>
          Click anywhere to exit overview ✕
        </div>

        <div className="showcase-spacer" style={{ height: 120 }} />
      </section>
    </>
  );
}